import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useAuth } from "../contexts/AuthContext";
import Topbar from "../components/Topbar";
import {
  Lock, Star, Plus, X, Search as SearchIcon, ChevronDown,
  LayoutGrid, List as ListIcon, Folder, FolderOpen, Archive,
  Sparkles,
} from "lucide-react";

export default function Projects() {
  const { currentWorkspaceId } = useWorkspace();
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filter, setFilter] = useState("my"); // my | starred | all | archived | group:<id>
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("updated"); // updated | name | due
  const [viewMode, setViewMode] = useState("grid"); // grid | list
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", brand: "", description: "" });

  const reload = () => {
    if (!currentWorkspaceId) return;
    Promise.all([
      api.get("/projects", { params: { workspace_id: currentWorkspaceId } }),
      api.get("/project-groups", { params: { workspace_id: currentWorkspaceId } }),
    ]).then(([p, g]) => {
      setProjects(p.data);
      setGroups(g.data);
    });
  };
  useEffect(reload, [currentWorkspaceId]);

  const star = async (id) => {
    await api.post(`/projects/${id}/star`);
    reload();
  };

  const createProject = async (e) => {
    e.preventDefault();
    if (!currentWorkspaceId) {
      alert("Please select a workspace before creating a project.");
      return;
    }
    try {
      await api.post("/projects", form, { params: { workspace_id: currentWorkspaceId } });
      setShowCreate(false);
      setForm({ name: "", code: "", brand: "", description: "" });
      reload();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to create project";
      alert(typeof msg === "string" ? msg : "Failed to create project");
    }
  };

  const filteredProjects = useMemo(() => {
    let list = [...projects];
    if (filter === "my") {
      // "My Projects" — owned-by or assigned-to me; for demo, keep all visible non-archived
      list = list.filter((p) => p.status !== "archived");
    } else if (filter === "starred") {
      list = list.filter((p) => p.starred);
    } else if (filter === "archived") {
      list = list.filter((p) => p.status === "archived");
    } else if (filter.startsWith("group:")) {
      const gid = filter.slice(6);
      list = list.filter((p) => p.project_group_id === gid);
    }
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((p) =>
        p.name.toLowerCase().includes(s) ||
        (p.code || "").toLowerCase().includes(s) ||
        (p.description || "").toLowerCase().includes(s)
      );
    }
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "due") list.sort((a, b) => (a.due_date || "z").localeCompare(b.due_date || "z"));
    else list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return list;
  }, [projects, filter, search, sort]);

  const counts = {
    my: projects.filter((p) => p.status !== "archived").length,
    starred: projects.filter((p) => p.starred).length,
    all: projects.length,
    archived: projects.filter((p) => p.status === "archived").length,
  };

  return (
    <div data-testid="projects-page" className="flex flex-col h-screen">
      <Topbar
        title="Projects"
        breadcrumb="Workspace"
        right={
          <button
            onClick={() => setShowCreate(true)}
            data-testid="new-project-btn"
            className="flex items-center gap-1.5 bg-[#D4A968] hover:bg-[#E8C285] text-black px-3 py-2 rounded-md text-sm font-semibold transition-colors"
          >
            <Plus className="w-4 h-4" /> New project
          </button>
        }
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Inner left rail */}
        <aside className="w-60 shrink-0 border-r border-zinc-800 p-3 overflow-y-auto" data-testid="projects-rail">
          <div className="flex items-center gap-2 px-2 py-1.5 text-[10px] uppercase font-mono tracking-widest text-zinc-500">
            <Folder className="w-3 h-3" /> Projects
          </div>
          <nav className="space-y-0.5 mt-1">
            {[
              { key: "my", label: "My Projects", count: counts.my, icon: FolderOpen },
              { key: "starred", label: "Starred Projects", count: counts.starred, icon: Star },
              { key: "all", label: "All Projects", count: counts.all, icon: Folder },
              { key: "archived", label: "Archived Projects", count: counts.archived, icon: Archive },
            ].map(({ key, label, count, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                data-testid={`rail-${key}`}
                className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-sm transition-colors ${
                  filter === key ? "bg-[#1B1B1F] text-white" : "text-zinc-400 hover:bg-[#141417] hover:text-white"
                }`}
              >
                <span className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </span>
                <span className="text-[10px] font-mono text-zinc-500">{count}</span>
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 px-2 py-1.5 mt-5 text-[10px] uppercase font-mono tracking-widest text-zinc-500">
            <Folder className="w-3 h-3" /> Groups
          </div>
          <nav className="space-y-0.5 mt-1">
            {groups.map((g) => {
              const c = projects.filter((p) => p.project_group_id === g.id).length;
              const k = `group:${g.id}`;
              return (
                <button
                  key={g.id}
                  onClick={() => setFilter(k)}
                  data-testid={`rail-group-${g.id}`}
                  className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-sm transition-colors ${
                    filter === k ? "bg-[#1B1B1F] text-white" : "text-zinc-400 hover:bg-[#141417] hover:text-white"
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0 truncate">
                    <span className="brand-dot shrink-0" style={{ background: g.color || "#D4A968" }} />
                    <span className="truncate">{g.name}</span>
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500">{c}</span>
                </button>
              );
            })}
            {groups.length === 0 && <div className="px-2 py-2 text-[11px] text-zinc-600">No groups yet.</div>}
          </nav>
        </aside>

        {/* Right pane */}
        <div className="flex-1 overflow-y-auto">
          {/* Filter bar */}
          <div className="flex items-center justify-between gap-3 px-6 py-3 border-b border-zinc-800 bg-[#0B0B0E]" data-testid="projects-filterbar">
            <div className="flex items-center gap-2 flex-1">
              <div className="relative flex-1 max-w-md">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
                <input
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search Projects"
                  data-testid="projects-search"
                  className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded-md pl-9 pr-3 py-2 text-sm text-white outline-none"
                />
              </div>
              <div className="relative">
                <select
                  value={sort} onChange={(e) => setSort(e.target.value)}
                  data-testid="projects-sort"
                  className="appearance-none bg-[#141417] border border-zinc-800 hover:border-zinc-700 rounded-md pl-3 pr-8 py-2 text-sm text-white outline-none cursor-pointer"
                >
                  <option value="updated">Last updated</option>
                  <option value="name">Name</option>
                  <option value="due">Due date</option>
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
              </div>
              <div className="text-[11px] font-mono text-zinc-500 px-2">
                {filteredProjects.length} of {counts.all}
              </div>
            </div>
            <div className="flex items-center gap-1 bg-[#141417] border border-zinc-800 rounded-md p-0.5">
              <button
                onClick={() => setViewMode("grid")}
                data-testid="view-grid"
                className={`p-1.5 rounded transition-colors ${viewMode === "grid" ? "bg-[#1B1B1F] text-white" : "text-zinc-500 hover:text-white"}`}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                data-testid="view-list"
                className={`p-1.5 rounded transition-colors ${viewMode === "list" ? "bg-[#1B1B1F] text-white" : "text-zinc-500 hover:text-white"}`}
              >
                <ListIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6">
            {viewMode === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="projects-grid">
                {filteredProjects.map((p) => (
                  <ProjectCard key={p.id} project={p} onOpen={() => nav(`/projects/${p.id}`)} onStar={() => star(p.id)} />
                ))}
              </div>
            ) : (
              <div className="bg-[#141417] border border-zinc-800 rounded-lg overflow-hidden" data-testid="projects-list">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 bg-[#0B0B0E] border-b border-zinc-800">
                      <th className="text-left px-4 py-2 w-8"></th>
                      <th className="text-left px-4 py-2">Name</th>
                      <th className="text-left px-4 py-2">Status</th>
                      <th className="text-right px-4 py-2">Tasks</th>
                      <th className="text-right px-4 py-2">Progress</th>
                      <th className="text-left px-4 py-2">Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((p) => {
                      const pct = p.task_total ? (p.task_done / p.task_total) * 100 : 0;
                      return (
                        <tr
                          key={p.id}
                          onClick={() => nav(`/projects/${p.id}`)}
                          className="border-b border-zinc-800 hover:bg-[#1B1B1F] cursor-pointer"
                          data-testid={`projects-list-row-${p.id}`}
                        >
                          <td className="px-4 py-2">
                            {p.is_private && <Lock className="w-3 h-3 text-zinc-500" />}
                          </td>
                          <td className="px-4 py-2 text-white">{p.name}</td>
                          <td className="px-4 py-2"><StatusBadge status={p.status} /></td>
                          <td className="px-4 py-2 text-right text-zinc-400 font-mono">{p.task_done}/{p.task_total}</td>
                          <td className="px-4 py-2 text-right text-zinc-400 font-mono">{Math.round(pct)}%</td>
                          <td className="px-4 py-2 text-zinc-400 font-mono">{p.due_date || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {filteredProjects.length === 0 && (
              <div className="text-center py-16 text-sm text-zinc-500" data-testid="projects-empty">
                No projects match. <button onClick={() => setShowCreate(true)} className="text-[#D4A968]">Create one →</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0B0B0E] border border-zinc-800 rounded-lg w-full max-w-md p-5" data-testid="new-project-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">New project</h3>
              <button onClick={() => setShowCreate(false)} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={createProject} className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-1">Name</label>
                <input
                  required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="form-project-name"
                  className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded px-2.5 py-2 text-sm text-white outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-1">Code</label>
                <input
                  value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="Vorion"
                  data-testid="form-project-code"
                  className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded px-2.5 py-2 text-sm text-white outline-none font-mono"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-1">Tier (optional)</label>
                <select
                  value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  data-testid="form-project-brand"
                  className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded px-2.5 py-2 text-sm text-white outline-none"
                >
                  <option value="">No tier</option>
                  <option value="luxury">Vorion Luxury</option>
                  <option value="elegance">Vorion Elegance</option>
                  <option value="resort">Vorion Resort</option>
                  <option value="smart">Vorion Smart</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-1">Description</label>
                <textarea
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded px-2.5 py-2 text-sm text-white outline-none resize-none"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="text-sm text-zinc-400 hover:text-white px-3 py-2">Cancel</button>
                <button type="submit" data-testid="form-project-submit" className="bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold px-4 py-2 rounded-md text-sm">Create</button>
              </div>
              <div className="text-[11px] text-zinc-600 pt-2 border-t border-zinc-800">
                Tip: bootstrap from a tier playbook in <a href="/templates" className="text-[#D4A968]">Templates</a>.
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    in_progress: { color: "#38BDF8", label: "In progress" },
    planning: { color: "#71717A", label: "No status", dashed: true },
    on_hold: { color: "#F59E0B", label: "On hold" },
    done: { color: "#10B981", label: "Done" },
    archived: { color: "#52525B", label: "Archived" },
  }[status] || { color: "#71717A", label: status || "—", dashed: true };
  return (
    <span
      data-testid="status-badge"
      className={`inline-block text-[10px] uppercase font-mono tracking-widest px-1.5 py-0.5 rounded ${
        cfg.dashed ? "border border-dashed" : "border"
      }`}
      style={{ color: cfg.color, borderColor: cfg.color + "55", background: cfg.color + "10" }}
    >
      {cfg.label}
    </span>
  );
}

function ProjectCard({ project, onOpen, onStar }) {
  const pct = project.task_total ? (project.task_done / project.task_total) * 100 : 0;
  const starred = project.starred;
  return (
    <div
      onClick={onOpen}
      data-testid={`project-card-${project.id}`}
      className="group bg-[#141417] border border-zinc-800 hover:border-zinc-600 rounded-lg overflow-hidden cursor-pointer transition-colors"
    >
      <div className="h-24 bg-gradient-to-br from-[#1B1B1F] to-[#0B0B0E] border-b border-zinc-800 flex items-center justify-center">
        <Folder className="w-8 h-8 text-zinc-700" strokeWidth={1.5} />
      </div>
      <div className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-bold text-white tracking-tight leading-snug flex items-center gap-1.5 min-w-0">
            {project.is_private && <Lock className="w-3 h-3 text-zinc-500 shrink-0" />}
            <span className="truncate">{project.name}</span>
          </h3>
          <button
            onClick={(e) => { e.stopPropagation(); onStar(); }}
            data-testid={`star-${project.id}`}
            className={starred ? "text-[#D4A968]" : "text-zinc-600 hover:text-[#D4A968] opacity-0 group-hover:opacity-100"}
          >
            <Star className={`w-3.5 h-3.5 ${starred ? "fill-[#D4A968]" : ""}`} />
          </button>
        </div>
        <div className="mt-2.5">
          <StatusBadge status={project.status} />
        </div>
        <div className="mt-3 flex items-center justify-between text-[10px] font-mono text-zinc-500">
          <span>{Math.round(pct)}% Completed</span>
          <span>{project.task_done}/{project.task_total} Tasks</span>
        </div>
        <div className="h-1 bg-[#0B0B0E] rounded mt-1 overflow-hidden">
          <div className="h-full bg-[#D4A968]" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}
